import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, mediaUrl } from "@/lib/api";
import { Truck, Shield, Wrench, Award, Camera, Sliders, ArrowRight, Search } from "lucide-react";
import ProductCard from "@/components/ProductCard";
import CategoryBlobCard from "@/components/CategoryBlobCard";

const BENEFIT_ICONS = [Truck, Award, Wrench, Shield];

export default function Home() {
  const [cats, setCats] = useState([]);
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState("");
  const [t, setT] = useState({});
  const nav = useNavigate();

  useEffect(() => {
    api.get("/categories").then(r => setCats(r.data));
    api.get("/products?limit=8").then(r => setProducts(r.data));
    api.get("/settings").then(r => setT(r.data || {})).catch(() => {});
  }, []);

  const benefits = [
    { icon: BENEFIT_ICONS[0], text: t.hp_benefit_1 || "Dërgesë e shpejtë në gjithë Kosovën" },
    { icon: BENEFIT_ICONS[1], text: t.hp_benefit_2 || "Produkte cilësore" },
    { icon: BENEFIT_ICONS[2], text: t.hp_benefit_3 || "Mbështetje teknike" },
    { icon: BENEFIT_ICONS[3], text: t.hp_benefit_4 || "Garanci për produktet" },
  ];

  const submit = (e) => { e.preventDefault(); if (q.trim()) nav(`/kerko?q=${encodeURIComponent(q)}`); };

  return (
    <div>
      {/* Hero */}
      <section className="relative bg-[#131E30] overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1620203853151-496c7228306c?w=1600" alt="" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-16 md:py-28">
          <div className="max-w-2xl">
            <div className="inline-block bg-[#FFD600] text-black text-xs font-bold uppercase tracking-widest px-3 py-1.5 mb-6">
              {t.hp_hero_badge || "HIDRAULIKË • TUBA • KOPLINGË"}
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-none mb-4">
              {t.hp_hero_title || "Zgjidhja juaj për"} <span className="text-[#FFD600]">{t.hp_hero_title_accent || "hidraulikë!"}</span>
            </h1>
            <p className="text-neutral-300 text-lg mb-8">
              {t.hp_hero_subtitle || "Cilësi e garantuar • Çmime konkurruese • Shërbim i shpejtë në Kosovë"}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mb-8">
              <Link to="/konfigurator" data-testid="hero-configurator-btn"
                className="hg-btn-primary px-6 py-4 font-bold uppercase tracking-wider text-sm inline-flex items-center justify-center gap-2">
                <Sliders size={18} /> {t.hp_cta_configurator || "Konfiguro Tubin"}
              </Link>
              <Link to="/kerko-me-foto" data-testid="hero-photo-btn"
                className="border-2 border-white text-white px-6 py-4 font-bold uppercase tracking-wider text-sm inline-flex items-center justify-center gap-2 hover:bg-white hover:text-black transition-colors">
                <Camera size={18} /> {t.hp_cta_photo || "Kërko pjesën me foto"}
              </Link>
            </div>
            <form onSubmit={submit} className="relative max-w-lg">
              <input value={q} onChange={e => setQ(e.target.value)} data-testid="hero-search-input"
                placeholder={t.hp_search_placeholder || "Çfarë po kërkon?"}
                className="w-full bg-white text-black pl-4 pr-14 py-4 text-base outline-none border-2 border-transparent focus:border-[#FFD600]" />
              <button type="submit" className="absolute right-0 top-0 h-full w-14 bg-[#FFD600] text-black flex items-center justify-center hover:bg-yellow-300 transition-colors">
                <Search size={20} />
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="border-y border-[#1F2B44] bg-[#131E30]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-2 md:grid-cols-4 divide-x divide-y md:divide-y-0 divide-[#262626]">
          {benefits.map((b, i) => (
            <div key={i} className="p-5 flex items-center gap-3">
              <div className="w-10 h-10 flex-shrink-0 bg-[#FFD600]/10 flex items-center justify-center text-[#FFD600]"><b.icon size={20} /></div>
              <span className="text-xs sm:text-sm text-neutral-300">{b.text}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Categories */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
        <div className="flex items-end justify-between mb-6">
          <h2 className="font-display text-3xl sm:text-4xl uppercase">{t.hp_categories_title || "Kategoritë kryesore"}</h2>
          <Link to="/kategori" className="text-sm text-[#FFD600] uppercase tracking-wider flex items-center gap-1">
            {t.hp_view_all || "Shiko të gjitha"} <ArrowRight size={14} />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 sm:gap-8 pb-4">
          {cats.filter(c => !c.parent_slug).map(c => (
            <CategoryBlobCard key={c.id} cat={c} testid={`cat-card-${c.slug}`} />
          ))}
        </div>
      </section>

      {/* Photo request banner */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-12">
        <Link to="/kerko-me-foto" data-testid="photo-request-banner"
          className="relative block overflow-hidden border border-[#FFD600] bg-[#131E30] hover:bg-[#1c1c1c] transition-colors">
          <div className="absolute inset-0 opacity-20">
            <img src="https://images.unsplash.com/photo-1619642751034-765dfdf7c58e?w=1200" alt="" className="w-full h-full object-cover" />
          </div>
          <div className="relative p-6 sm:p-8 flex items-center gap-6">
            <div className="w-14 h-14 sm:w-20 sm:h-20 bg-[#FFD600] text-black flex items-center justify-center flex-shrink-0">
              <Camera size={32} />
            </div>
            <div className="flex-1">
              <div className="font-display text-2xl sm:text-3xl uppercase leading-tight">{t.hp_photo_banner_title || "Nuk e di cilën pjesë të duhet?"}</div>
              <div className="text-sm text-neutral-400 mt-1">{t.hp_photo_banner_subtitle || "Dërgo fotografinë e pjesës ose etiketës dhe ne përpiqemi ta gjejmë."}</div>
            </div>
            <div className="hidden sm:flex hg-btn-primary px-6 py-3 uppercase text-sm">{t.hp_photo_banner_cta || "Dërgo Foto"}</div>
          </div>
        </Link>
      </section>

      {/* Featured Products */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-16">
        <div className="flex items-end justify-between mb-6">
          <h2 className="font-display text-3xl sm:text-4xl uppercase">{t.hp_featured_title || "Produktet më të shitura"}</h2>
          <Link to="/kategori" className="text-sm text-[#FFD600] uppercase tracking-wider">{t.hp_view_all || "Shiko të gjitha"}</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
          {products.map(p => <ProductCard key={p.id} p={p} />)}
        </div>
      </section>
    </div>
  );
}
